import { Database, ChevronRight, History, ArrowUpRight, Save, PanelsTopLeft } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useNavigateBack } from '@/hooks/useNavigateBack'
import { Button } from '@/components/ui/button'
import { useTranslation } from '@/i18n'
import FileNameWithCopy from '@/components/FileNameWithCopy'
import SldViewSwitch from './SldViewSwitch'
import type { SldGraphView, SldGraphViews } from './sldGraphViews'

interface GraphEditorNavbarV2Props {
  documentName: string
  isSaved: boolean
  isSaving: boolean
  isModified: boolean
  onSave: () => void
  onOpenInBoard?: () => void
  onHistoryClick?: () => void
  isHistoryActive?: boolean
  /** If set, breadcrumb shows board name instead of "Data Catalog" */
  fromBoardName?: string | null
  /** An SLD page's two graphs (VISN-304), shown as an Annotation | Parsed switch. */
  sldViews?: SldGraphViews | null
  /** Open the picked view of the same SLD page. */
  onSldViewSelect?: (view: SldGraphView) => void
}

export default function GraphEditorNavbarV2({
  documentName,
  isSaved,
  isSaving,
  isModified,
  onSave,
  onOpenInBoard,
  onHistoryClick,
  isHistoryActive,
  fromBoardName,
  sldViews,
  onSldViewSelect,
}: GraphEditorNavbarV2Props) {
  const navigate = useNavigate()
  const navigateBackDefault = useNavigateBack()
  const { t } = useTranslation()

  // Detect if user came from a board (check referrer URL path)
  const cameFromBoard = typeof document !== 'undefined' && document.referrer && (() => {
    try {
      const ref = new URL(document.referrer)
      return ref.pathname.startsWith('/board/')
    } catch { return false }
  })()
  const effectiveBoardName = fromBoardName || (cameFromBoard ? 'Board' : null)

  return (
    <div className="flex items-center justify-between px-6 py-[18px] border-b border-[#e5e5e5] dark:border-dark-border bg-white dark:bg-dark-card shrink-0">
      {/* Left: Breadcrumb */}
      <div className="flex items-center gap-2.5 min-w-0">
        <button
          type="button"
          onClick={effectiveBoardName ? navigateBackDefault : () => navigate('/data-catalog')}
          className="flex items-center gap-2 shrink-0 text-muted-foreground hover:text-foreground transition-colors"
        >
          {effectiveBoardName ? (
            <PanelsTopLeft className="w-[18px] h-[18px]" />
          ) : (
            <Database className="w-[18px] h-[18px]" />
          )}
          <span className="text-base">{effectiveBoardName || t('nav:menu.data', 'Data')}</span>
        </button>

        <ChevronRight className="w-[15px] h-[15px] text-muted-foreground shrink-0" />

        {/* aria-live: the slot starts as a screen-reader "Loading…" announcement
            and updates to the resolved filename, so assistive tech is told both
            that a name is loading and what it turned out to be. */}
        <div className="flex items-center gap-2 min-w-0" aria-live="polite" aria-busy={!documentName}>
          {documentName ? (
            <FileNameWithCopy name={documentName} className="text-base font-normal" />
          ) : (
            // Name resolves a couple seconds after the editor mounts. Show a
            // skeleton placeholder rather than the page-title fallback so the
            // crumb never reads "Data Catalog > Data Catalog". The visual pulse
            // is aria-hidden; the sr-only text carries the loading state.
            <>
              <span className="sr-only">
                {t('data_catalog:graph_editor.loading_name', { defaultValue: 'Loading document name…' })}
              </span>
              <span
                aria-hidden
                className="h-4 w-32 rounded bg-gray-200 dark:bg-dark-accent animate-pulse"
              />
            </>
          )}
        </div>

        {sldViews && onSldViewSelect && (
          <div className="ml-2">
            <SldViewSwitch views={sldViews} onSelect={onSldViewSelect} />
          </div>
        )}
      </div>

      {/* Right: Actions */}
      <div className="flex items-center gap-3 shrink-0">
        {/* Status badge */}
        {isSaving ? (
          <span className="bg-gray-100 text-gray-500 text-xs font-medium px-2 py-0.5 rounded-full animate-pulse">
            {t('common:buttons.auto_saving', { defaultValue: 'Saving...' })}
          </span>
        ) : isModified ? (
          <span className="bg-[#f5f5f5] border border-[#e5e5e5] text-[#171717] text-xs font-medium px-2 py-0.5 rounded-full">
            {t('common:buttons.unsaved', { defaultValue: 'Unsaved' })}
          </span>
        ) : isSaved ? (
          <span className="bg-green-100 text-green-700 text-xs font-medium px-2 py-0.5 rounded-full">
            {t('common:buttons.saved', { defaultValue: 'Saved' })}
          </span>
        ) : null}

        {/* History */}
        <Button
          variant="outline"
          size="icon"
          className={`size-8 ${isHistoryActive ? 'border-primary text-primary bg-primary/5' : ''}`}
          onClick={onHistoryClick}
        >
          <History className="w-4 h-4" />
        </Button>

        {/* Open in Board */}
        {onOpenInBoard && (
          <Button variant="outline" size="sm" className="h-8 gap-1.5" onClick={onOpenInBoard}>
            <span className="text-sm font-medium">{t('data_catalog:graph_editor.open_in_board', { defaultValue: 'Open in Board' })}</span>
            <ArrowUpRight className="w-4 h-4" />
          </Button>
        )}

        {/* Save Changes */}
        <Button size="sm" className="h-8 gap-1.5" disabled={!isModified} onClick={onSave}>
          <Save className="w-4 h-4" />
          <span className="text-sm font-medium">{t('data_catalog:graph_editor.controls.save_changes', { defaultValue: 'Save Changes' })}</span>
        </Button>
      </div>
    </div>
  )
}
